import type { RouteDef } from '../../app/routes'
import type { WrittenPage } from '../sections'

/**
 * The "Transforming Pain into Power" e-book's own page.
 *
 * Built to the design of the digitopia.co page it replaces
 * (`/impact-labs/ebooks/transforming-pain-into-power/`), sent as four
 * screenshots on 2026-08-28 with *"referans alarak ilgili sayfayı tastamam
 * şekilde, aynısını, tıpkısını tasarla"*. Band for band that page's: the
 * two-column hero with the erupting-volcano artwork beside it, the grey "Why
 * Download This Ebook?" card with the pair of cover mockups and the four
 * promises, the "What You'll Gain" card with the four bolded gains, and the
 * centred "Join the Movement to Transform Leadership" ask. The copy is that
 * page's copy, verbatim.
 *
 * Seventh e-book page after Transform Better, Agentic AI, Sustainability
 * Innovation, Measurement, AI in Fashion Retail and The Leader's Guide to
 * Generative AI, and it follows `ebook-generative-ai.ts` in every place the
 * reference and this site disagree — the same three deliberate departures, for
 * the same reasons:
 *
 *  * **The eyebrow is the site's own**, over the copy column, rather than
 *    digitopia.co's small left-pinned "FROM BURNOUT TO BREAKTHROUGH" label.
 *    Every other hero here is that shape, and the words are unchanged.
 *  * **The download is a direct link to the PDF, not a gated form.** The
 *    reference puts a nine-field HubSpot form between the reader and the file;
 *    no form on this site submits anywhere yet, so reproducing it would mean the
 *    page's only call to action silently drops every lead. The `check` below
 *    says so, as the sibling pages' do.
 *  * **A testimonial closes the page**, which the reference does not have — it
 *    is what the sibling e-book pages do, asked for on 2026-08-28 (*"en aşağıda
 *    önceden konuştuğumuz gibi testimonial template ini kullanalım"*). The card
 *    is Lila Kağıt's: of the twenty in `content/testimonials.ts` it is the one
 *    about **mobilizing departments towards common goals**, accountability
 *    across them and strategic alignment — which is precisely what this book
 *    promises a leader worn down by silos and bureaucracy. Its words are the
 *    Hall of Fame's; that file is where a change to them belongs.
 *
 * **The closing line of the first card sits with the paragraph above the
 * bullets, not under them.** On the reference, "Don't let leadership pressures
 * hold you back…" comes after the four promises; a card renders `body` before
 * `points` and nothing about that ordering is worth a new section field, so the
 * sentence keeps its words and loses four lines of position.
 *
 * **The slug is confirmed.** The screenshots were captured from
 * `digitopia.co/impact-labs/ebooks/transforming-pain-into-power/` and carry that
 * address in their filenames — which is also what the catalogue's title rule
 * derived, the first of the checked slugs where the two agree since Agentic AI.
 */
export const route: RouteDef = {
  id: 'ebookTransformingPainIntoPower',
  path: 'impact-labs/ebooks/transforming-pain-into-power',
  priority: 0.7,
}

/**
 * The PDF, in the `website-documents` bucket rather than in `public/` — the same
 * object `content/ebooks.ts` already lists for this e-book's card, spelled out
 * here because this page needs it on four buttons and that file's `pdf()` helper
 * is private to the catalogue. A page's own address and file are local constants
 * in its own module, not keys in `content/pages/links.ts`: that file is for CTAs
 * shared between pages, and two concurrent runs adding two e-books to it wrote
 * the same lines.
 */
const PDF
  = 'https://njdxtkezxgkrmwwhzcne.supabase.co/storage/v1/object/public/website-documents/ebook/'
  + 'ebc82665-fef9-4e2f-8f12-b1f54703d0a6-Transforming%20Pain%20into%20Power.pdf'

export const ebookTransformingPainIntoPower: WrittenPage = {
  navLabel: 'Transforming Pain into Power',
  title: 'Transforming Pain into Power',
  description:
    'Break the cycle of inefficiency and executive suffering, and turn challenges into opportunities for growth and leadership. Download the e-book from Digitopia Impact Labs.',
  sections: [
    {
      kind: 'hero',
      eyebrow: 'From Burnout to Breakthrough',
      headline: 'Transforming Pain into Power',
      body: [
        'Discover how to break the cycle of inefficiency and executive suffering, and turn '
          + 'challenges into opportunities for growth and leadership.',
      ],
      ctas: [{ label: 'Download', href: PDF }],
      photo: {
        src: '/photo/transforming-pain-ebook-artwork.webp',
        alt:
          'The e-book’s cover artwork: a volcano erupting into a starfield, embers rising from '
          + 'the crater and lava running down its flanks.',
      },
      // The artwork is square and the site's hero box is 3:2, which would take
      // the top and bottom off the eruption.
      photoFrame: 'square',
      /*
        Clause 1 — THE DOWNLOAD IS NOT GATED — resolved 2026-08-30. It said no
        form on this site submits anywhere and the Send button is disabled on
        purpose. Both stopped being true today: the form posts to
        `website-form-lead`, the submission opens a task on its page's own
        counterpart, and every Download here carries `data-gated-download`, so
        the reader is asked for an email and the PDF is sent to it. No HubSpot
        form id is needed for any of it. What is left is clause 2.
      */
      check:
        'THE ARTWORK IS 768–900px WIDE. Those are the largest copies of the '
        + 'cover artwork and the two mockups anybody has sent; every other hero on '
        + 'the site is 2560px, so this one is painted at roughly its own width on a '
        + 'laptop with nothing left over for a retina screen. If bigger exports '
        + 'exist wherever the covers were made:\n'
        + '     node apps/website/scripts/attach.mjs <file> photo-src/transforming-pain-ebook-artwork.webp\n'
        + '     pnpm --filter @digitopia/website images\n'
        + '   and nothing in this file changes.',
    },
    {
      kind: 'cta',
      tone: 'card',
      // Mockup left, offer right — the reference's order, and it alternates with
      // the card under it.
      flip: true,
      heading: 'Why Download This Ebook?',
      body: [
        'Executives often face challenges like inefficiency, burnout, and complex organizational '
          + 'dynamics. This ebook provides actionable insights to help you turn these struggles '
          + 'into strengths. You’ll find valuable guidance on how to:',
        'Don’t let leadership pressures hold you back. Unlock the tools to transform your pain '
          + 'points into powerful breakthroughs.',
      ],
      points: [
        'Break free from corporate inertia and drive meaningful change.',
        'Overcome executive isolation and foster strong, collaborative teams.',
        'Cut through bureaucracy and make impactful decisions.',
        'Navigate talent challenges and build a resilient, engaged workforce.',
      ],
      ctas: [{ label: 'Download the Playbook', href: PDF }],
      photo: {
        src: '/photo/transforming-pain-ebook-covers.webp',
        alt:
          'Two copies of Transforming Pain into Power, one behind the other, on a white '
          + 'background.',
      },
    },
    {
      kind: 'cta',
      tone: 'card',
      heading: 'What You’ll Gain',
      points: [
        'Practical Strategies: Learn how to dismantle organizational silos, cut through '
          + 'bureaucracy, and navigate complex leadership challenges with confidence.',
        'Leadership Insights: Gain a deeper understanding of how to overcome executive suffering '
          + 'and lead with purpose, clarity, and resilience.',
        'Real-World Case Studies: Discover how other leaders have successfully transformed their '
          + 'organizations by applying the principles and strategies outlined in this ebook.',
        'Actionable Takeaways: Walk away with concrete steps you can implement immediately to '
          + 'enhance your leadership effectiveness and drive organizational success.',
      ],
      ctas: [{ label: 'Download the Playbook', href: PDF }],
      photo: {
        src: '/photo/transforming-pain-ebook-stack.webp',
        alt: 'A stack of printed copies of Transforming Pain into Power seen from above.',
      },
    },
    {
      kind: 'prose',
      align: 'center',
      heading: 'Join the Movement to Transform Leadership',
      sub:
        'Ready to break the cycle of inefficiency and lead with confidence? Download the ebook '
        + 'today and start your journey from burnout to breakthrough.',
      cta: { label: 'Download the Playbook', href: PDF },
    },
    {
      kind: 'quotes',
      variant: 'fame',
      heading: 'What our customers say.',
      items: [
        {
          // The Hall of Fame's Lila Kağıt card, word for word —
          // `content/testimonials.ts` is where these words are edited, and the
          // Turkish page carries its own saying of them.
          quote:
            'The digital maturity assessment has been a crucial tool in mobilizing our '
            + 'departments towards common goals. With the guidance of a moderator and the '
            + 'competitive insights provided, our teams were able to position themselves within '
            + 'the market and take ownership of their transformation steps. We ensured '
            + 'accountability across departments and achieved strategic alignment.',
          source: 'Tarkan Ateşoğlu, Information Technology Group Manager',
          company: 'Lila Kağıt',
          logo: { name: 'Lila Kağıt', src: '/logo/lilakagitcard.png' },
          portrait: { src: '/photo/portrait-tarkan-atesoglu.png', alt: 'Tarkan Ateşoğlu' },
        },
      ],
    },
  ],
}
